import type { ReactNode } from 'react'
import type { RichBlock, InlineContext } from '../types'
import { CODE_FENCE_RE, BOX_DRAW_RE, HEADING_RE, BLOCKQUOTE_RE } from '../constants'

export function isLabelLine(text: string) {
  return /^(\*\*[^*]+:\*\*|[A-Z][A-Za-z0-9 /&()-]{1,40}:)$/.test(text.trim())
}

// Markdown-table separator row, e.g. `|---|:---:|---|`
export function isTableSeparator(text: string) {
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(text.trim())
}

// Split a `| a | b | c |` row into trimmed cells. Tolerates missing
// leading/trailing pipes, which is technically valid GFM.
export function splitTableRow(text: string): string[] {
  return text
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
}

// Inline tokenizer that handles, in priority order:
//   `code`            inline code
//   **bold**          bold
//   *italic*          italic (asterisk; bold matches first)
//   _italic_          italic (underscore)
//   [text](url)       markdown links
//   https://...       bare URLs (auto-linked)
// Anything else is rendered as plain text. Italics require non-whitespace
// immediately inside the delimiters so prose like "2 * 3 * 4" or "use _ for
// padding" doesn't get misparsed.
export function renderInline(text: string, ctx?: InlineContext): ReactNode[] {
  // Citation tokens come first so the scanner picks them up before the
  // markdown-link branch. We accept several formats Gemini emits in the
  // wild even though the prompt asks for `[call_N]`:
  //   [call_1]                — preferred format
  //   [call1]                 — model drops the underscore
  //   [call_1, call_2]        — multiple ids in one bracket
  //   [call1,call2]           — same, no spaces, no underscores
  //   [1]                     — plain number; only treated as a citation
  //                             when N is within the message's tool-call
  //                             count (to avoid matching list items)
  // Each id renders as its own clickable chip so a multi-cite expands
  // into [1][2] visually.
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*(?:\S|\S[^*\n]*\S)\*)|(_(?:\S|\S[^_\n]*\S)_)|(\[call_?\d+(?:\s*,\s*call_?\d+)*\])|(\[\d+(?:\s*,\s*\d+)*\])|(\[[^\]]+\]\([^)\s]+\))|(https?:\/\/[^\s)<>]+)/g
  const nodes: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let keySeq = 0

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(
        <span key={`t-${keySeq++}`}>{text.slice(lastIndex, match.index)}</span>
      )
    }

    const token = match[0]

    // Citation chip(s) — link each call_id to its inspectable tool-call
    // card. Tolerant of `[call_1]`, `[call1]`, `[call_1, call_2]`,
    // `[call1,call2]`, AND plain `[1]` / `[1, 2]` when the numbers are
    // within the message's tool-call count (to avoid matching list-item
    // numbers). Format on screen: `[N]` (footnote-style). Earlier
    // versions used `#N`, but that visually collided with GitHub
    // issue-number syntax (`mchauhanx/ytest#1`).
    const callPrefixed = token.match(/^\[(call_?\d+(?:\s*,\s*call_?\d+)*)\]$/)
    const plainNumeric = token.match(/^\[(\d+(?:\s*,\s*\d+)*)\]$/)
    let citationMatch: RegExpMatchArray | null = null
    if (callPrefixed) {
      citationMatch = callPrefixed
    } else if (plainNumeric && ctx?.citableCallIds) {
      // Only treat `[N]` as a citation if N maps to a real call id in
      // this message. Otherwise it's just a list item and should render
      // as plain text.
      const candidateIds = plainNumeric[1].split(',').map((s) => s.trim())
      const allKnown = candidateIds.every((n) =>
        ctx.citableCallIds?.has(`call_${n}`),
      )
      if (allKnown) {
        citationMatch = plainNumeric
      }
    }
    if (citationMatch) {
      const idsRaw = citationMatch[1].split(',').map((s) => s.trim())
      const chips: ReactNode[] = []
      idsRaw.forEach((rawId, idx) => {
        // Normalise to the canonical `call_N` form for lookup, while
        // preserving the rendered `[N]`.
        const numMatch = rawId.match(/^call_?(\d+)$/)
        if (!numMatch) return
        const number = numMatch[1]
        const canonical = `call_${number}`
        const known = ctx?.citableCallIds?.has(canonical) ?? false
        if (known && ctx?.messageId) {
          chips.push(
            <a
              key={`cite-${keySeq++}-${idx}`}
              href={`#tool-call-${ctx.messageId}-${canonical}`}
              onClick={(e) => {
                e.preventDefault()
                const el = document.getElementById(`tool-call-${ctx.messageId}-${canonical}`)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  el.classList.add('ring-2', 'ring-ring')
                  setTimeout(() => el.classList.remove('ring-2', 'ring-ring'), 1200)
                }
              }}
              className="ml-0.5 inline-flex items-center rounded bg-foreground/[0.08] px-1.5 py-0.5 align-super text-[0.7em] font-medium text-muted-foreground no-underline hover:bg-foreground/[0.12] hover:text-foreground"
              title={`Jump to tool call ${number}`}
            >
              [{number}]
            </a>
          )
        } else {
          chips.push(
            <span
              key={`cite-${keySeq++}-${idx}`}
              className="ml-0.5 align-super text-[0.7em] text-subtle-foreground"
              title={`Unknown tool call ${number}`}
            >
              [{number}]
            </span>
          )
        }
      })
      nodes.push(...chips)
      lastIndex = match.index + token.length
      continue
    }

    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={`c-${keySeq++}`}
          className="rounded bg-foreground/[0.10] px-1.5 py-0.5 text-[0.9em] font-medium text-foreground"
        >
          {token.slice(1, -1)}
        </code>
      )
    } else if (token.startsWith('**')) {
      nodes.push(
        <strong key={`b-${keySeq++}`} className="font-semibold text-foreground">
          {token.slice(2, -2)}
        </strong>
      )
    } else if (token.startsWith('*')) {
      // Italic via single asterisks. Bold above already consumed `**...**`,
      // so a `*` token here is unambiguous.
      nodes.push(
        <em key={`i-${keySeq++}`} className="italic">
          {token.slice(1, -1)}
        </em>
      )
    } else if (token.startsWith('_')) {
      nodes.push(
        <em key={`i-${keySeq++}`} className="italic">
          {token.slice(1, -1)}
        </em>
      )
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/)
      if (linkMatch) {
        nodes.push(
          <a
            key={`l-${keySeq++}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noreferrer noopener"
            className="text-accent underline hover:text-accent-soft-foreground"
          >
            {linkMatch[1]}
          </a>
        )
      } else {
        nodes.push(<span key={`l-${keySeq++}`}>{token}</span>)
      }
    } else {
      // Bare URL — strip trailing punctuation that's almost never part of a link.
      let url = token
      let trailing = ''
      while (url.length > 0 && /[.,;:!?]$/.test(url)) {
        trailing = url.slice(-1) + trailing
        url = url.slice(0, -1)
      }
      nodes.push(
        <a
          key={`u-${keySeq++}`}
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="break-all text-accent underline hover:text-accent-soft-foreground"
        >
          {url}
        </a>
      )
      if (trailing) {
        nodes.push(<span key={`t-${keySeq++}`}>{trailing}</span>)
      }
    }

    lastIndex = match.index + token.length
  }

  if (lastIndex < text.length) {
    nodes.push(<span key={`t-${keySeq++}`}>{text.slice(lastIndex)}</span>)
  }

  return nodes
}

export function parseRichText(content: string): RichBlock[] {
  const lines = content
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd())

  const blocks: RichBlock[] = []
  let paragraph: string[] = []
  let bulletItems: string[] = []
  let numberedItems: string[] = []
  let activeListType: 'bullet' | 'numbered' | null = null

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', lines: paragraph })
      paragraph = []
    }
  }

  const flushBullets = () => {
    if (bulletItems.length > 0) {
      blocks.push({ type: 'bullet-list', items: bulletItems })
      bulletItems = []
    }
    if (activeListType === 'bullet') {
      activeListType = null
    }
  }

  const flushNumbered = () => {
    if (numberedItems.length > 0) {
      blocks.push({ type: 'numbered-list', items: numberedItems })
      numberedItems = []
    }
    if (activeListType === 'numbered') {
      activeListType = null
    }
  }

  let i = 0
  while (i < lines.length) {
    const rawLine = lines[i]
    const line = rawLine.trim()

    // Fenced code block — handled BEFORE the empty-line / table / list /
    // label / paragraph paths so the body is captured verbatim, never
    // misinterpreted as bullets, tables, etc. Critically the body lines
    // come from `lines` (already trim-end'd) so newlines are preserved.
    const fenceOpen = line.match(CODE_FENCE_RE)
    if (fenceOpen) {
      flushParagraph()
      flushBullets()
      flushNumbered()
      const language = fenceOpen[1] || null
      const codeLines: string[] = []
      i++
      while (i < lines.length) {
        const innerRaw = lines[i]
        if (CODE_FENCE_RE.test(innerRaw.trim())) {
          // Closing fence — consume it and stop.
          i++
          break
        }
        codeLines.push(innerRaw)
        i++
      }
      // We push the block even if the closing fence never arrived (which
      // happens during streaming, before the model has emitted it). This
      // way the user sees an in-progress code block instead of the body
      // collapsing into prose.
      blocks.push({ type: 'code-block', language, lines: codeLines })
      continue
    }

    if (!line) {
      flushParagraph()
      // Don't flush lists on blank lines if the next non-blank line
      // continues the same list type. This handles "loose lists" where
      // items are separated by blank lines (common in Jira ADF output).
      const nextNonBlank = lines.slice(i + 1).find(l => l.trim() !== '')
      const nextIsBullet = nextNonBlank && /^\s*[-*•]\s+/.test(nextNonBlank)
      const nextIsNumbered = nextNonBlank && /^\s*\d+\.\s+/.test(nextNonBlank)

      if (!(activeListType === 'bullet' && nextIsBullet)) {
        flushBullets()
      }
      if (!(activeListType === 'numbered' && nextIsNumbered)) {
        flushNumbered()
      }
      i++
      continue
    }

    // Markdown table: header row containing pipes, immediately followed by a
    // separator row. Greedily consume subsequent `|...|` rows as data.
    if (
      line.includes('|') &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      flushParagraph()
      flushBullets()
      flushNumbered()

      const headers = splitTableRow(line)
      const rows: string[][] = []
      i += 2 // skip header + separator
      while (i < lines.length) {
        const candidate = lines[i].trim()
        if (!candidate || !candidate.includes('|')) break
        rows.push(splitTableRow(candidate))
        i++
      }
      blocks.push({ type: 'table', headers, rows })
      continue
    }

    // Implicit code block — any line containing a box-drawing character
    // (├ └ │ ─ ┌ ┐ ┘ ┴ ┬ ┤ etc.) is treated as the start of an ASCII
    // tree or diagram. Greedily consume consecutive box-drawing lines
    // verbatim. This is a safety net for when the model produces a
    // tree without wrapping it in ```text fences. Runs AFTER the table
    // check so well-formed markdown tables aren't accidentally caught.
    if (BOX_DRAW_RE.test(rawLine)) {
      flushParagraph()
      flushBullets()
      flushNumbered()
      const treeLines: string[] = []
      while (i < lines.length && BOX_DRAW_RE.test(lines[i])) {
        treeLines.push(lines[i])
        i++
      }
      blocks.push({ type: 'code-block', language: 'text', lines: treeLines })
      continue
    }

    // ATX heading (`# H1` through `###### H6`).
    const headingMatch = line.match(HEADING_RE)
    if (headingMatch) {
      flushParagraph()
      flushBullets()
      flushNumbered()
      const level = Math.min(6, headingMatch[1].length) as 1 | 2 | 3 | 4 | 5 | 6
      blocks.push({ type: 'heading', level, content: headingMatch[2] })
      i++
      continue
    }

    // Blockquote — greedily consume consecutive `> ` lines.
    if (BLOCKQUOTE_RE.test(line)) {
      flushParagraph()
      flushBullets()
      flushNumbered()
      const quoteLines: string[] = []
      while (i < lines.length) {
        const innerMatch = lines[i].trim().match(BLOCKQUOTE_RE)
        if (!innerMatch) break
        quoteLines.push(innerMatch[1])
        i++
      }
      blocks.push({ type: 'blockquote', lines: quoteLines })
      continue
    }

    if (isLabelLine(line)) {
      flushParagraph()
      flushBullets()
      flushNumbered()
      blocks.push({ type: 'label', content: line })
      i++
      continue
    }

    const bulletMatch = line.match(/^[-*•]\s+(.+)$/)
    if (bulletMatch) {
      if (isLabelLine(bulletMatch[1].trim())) {
        flushParagraph()
        flushBullets()
        flushNumbered()
        blocks.push({ type: 'label', content: bulletMatch[1].trim() })
        i++
        continue
      }
      flushParagraph()
      if (activeListType === 'numbered') {
        flushNumbered()
      }
      bulletItems.push(bulletMatch[1].trim())
      activeListType = 'bullet'
      i++
      continue
    }

    const numberedMatch = line.match(/^\d+\.\s+(.+)$/)
    if (numberedMatch) {
      flushParagraph()
      if (activeListType === 'bullet') {
        flushBullets()
      }
      numberedItems.push(numberedMatch[1].trim())
      activeListType = 'numbered'
      i++
      continue
    }

    if (activeListType === 'bullet' && bulletItems.length > 0) {
      const lastIndex = bulletItems.length - 1
      bulletItems[lastIndex] = `${bulletItems[lastIndex]} ${line}`.trim()
      i++
      continue
    }

    if (activeListType === 'numbered' && numberedItems.length > 0) {
      const lastIndex = numberedItems.length - 1
      numberedItems[lastIndex] = `${numberedItems[lastIndex]} ${line}`.trim()
      i++
      continue
    }

    flushBullets()
    flushNumbered()
    paragraph.push(line)
    i++
  }

  flushParagraph()
  flushBullets()
  flushNumbered()

  return blocks
}
